import { stringFormat, T } from 'src/shared/helpers';

// LA VERITÀ SULLE TRASFORMATE STA QUI, e una volta sola: quanto vale un numero del pannello
// (gradi o radianti), dove il motore tiene davvero l'orientamento, e in che spazio si trova
// l'oggetto che si sta guardando. Il pannello, l'albero e chi scrive sulla mesh la leggono da
// questo file — averne due copie vorrebbe dire due risposte diverse alla stessa domanda.

// I GRADI STANNO NEL PANNELLO, I RADIANTI NEL MOTORE. Il campo della rotazione dice «°» e quel
// numero finiva tale e quale dentro `mesh.rotation`, che Babylon legge in radianti: battere 1
// girava di 57 gradi, e i 57 gradi che il motore aveva li rimandava indietro come «57».
const toDegrees = (radians: number) => (radians * 180) / Math.PI;
export const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

// OLTRE IL GIRO SI RICOMINCIA DA CAPO (QA, 2026-10-15): 365 è 5, 682 è 322, -370 è -10 — come in ogni
// editor 3D. Il giro intero resta com'è: 360 e -360 non superano niente. L'orientamento è lo stesso,
// cambia solo il numero che si legge. Vale per ciò che arriva dal campo; i valori già salvati in una
// scena non si riscrivono. L'arrotondamento toglie la coda della sottrazione in virgola mobile
// (360.1 - 360 = 0.10000000000002274).
export const wrapDegrees = (degrees: number) => (Math.abs(degrees) > 360 ? Number((degrees - 360 * Math.trunc(degrees / 360)).toFixed(6)) : degrees);

// L'ORIENTAMENTO PUÒ STARE IN DUE POSTI e ne comanda uno solo. Un modello importato porta spesso
// un `rotationQuaternion` — il caricatore glTF lo mette su ogni nodo — e finché c'è è QUELLO a
// decidere: `rotation` resta a zero e non la guarda nessuno. Leggere la terna sbagliata voleva dire
// scrivere 0 · 0 · 0 sotto una cosa visibilmente girata.
export const readRotation = (mesh: any): [number, number, number] => {
    const euler = mesh?.rotationQuaternion?.toEulerAngles?.() ?? mesh?.rotation;
    return euler ? [toDegrees(euler.x), toDegrees(euler.y), toDegrees(euler.z)] : [0, 0, 0];
};

// LE TRASFORMATE SI CHIEDONO AL MOTORE, non alla copia tenuta nell'albero. Fra una lettura e
// l'altra le cambiano il gizmo e l'auto-scala del VTO, e nessuno dei due passa di qui: la copia
// resta ferma ai numeri del caricamento mentre l'oggetto in scena è altrove.
export const liveTransform = (mesh: any) => ({
    position: (mesh?.position ? [mesh.position.x, mesh.position.y, mesh.position.z] : [0, 0, 0]) as [number, number, number],
    rotation: readRotation(mesh),
    scaling: (mesh?.scaling ? [mesh.scaling.x, mesh.scaling.y, mesh.scaling.z] : [1, 1, 1]) as [number, number, number]
});

// COM'ERA QUANDO L'ABBIAMO VISTA LA PRIMA VOLTA. «Reset transform» non riporta all'identità: un
// figlio importato non è nato a 0,0,0 — ce l'ha messo il modellatore, e il caricatore glTF ci ha
// messo sopra il suo adattamento. Azzerare quei numeri buttava via tutti e due, e non c'era modo di
// tornare indietro.
//
// LA MEMORIA È DELLA MESH, non del suo nodo nell'albero: l'albero si ricostruisce a ogni evento del
// motore, la mesh no. Con una `WeakMap` una mesh cancellata si porta via la propria riga.
//
// SI SEGNA ALLA PRIMA OCCHIATA e non si riscrive più: chi costruisce l'albero passa di qui appena la
// scena è caricata, prima che si possa toccare qualcosa.
const loadedTransforms = new WeakMap<object, ReturnType<typeof liveTransform>>();

export const rememberLoadedTransform = (mesh: any) => {
    if (!mesh || loadedTransforms.has(mesh)) return;
    loadedTransforms.set(mesh, liveTransform(mesh));
};

/** Com'era al caricamento. Di una mesh mai vista — non dovrebbe capitare — resta l'identità. */
export const loadedTransform = (mesh: any) =>
    loadedTransforms.get(mesh) ?? {
        position: [0, 0, 0] as [number, number, number],
        rotation: [0, 0, 0] as [number, number, number],
        scaling: [1, 1, 1] as [number, number, number]
    };

// UN MODELLO IMPORTATO NON ARRIVA DRITTO. I file glTF/glb descrivono la scena in un sistema di
// coordinate destrorso, Babylon la disegna in uno mancino, e il caricatore mette la differenza su
// un nodo solo: lo gira di 180° e gli inverte un asse di scala. Il core lo rinomina `*_transforms`
// e gli costruisce sopra una radice pulita, ma quel nodo resta dov'è — ed è uno SPECCHIO.
//
// In uno specchio le rotazioni si vedono rovesciate: sotto quel nodo battere +1 fa girare
// l'oggetto dalla parte opposta, mentre sulla radice — che sta sopra lo specchio — tutto si
// comporta come ci si aspetta. È la ragione per cui «sui figli la rotazione fa cose strane», ed è
// una proprietà del ramo, non del campo che si sta toccando.
//
// Qui non si corregge niente: si riconosce la situazione e si dice com'è. Raddrizzare i versi
// vorrebbe dire cambiare i numeri che i clienti hanno sotto gli occhi da anni, e quella è una
// decisione di prodotto, non un dettaglio di questo file.

type Axis = 'x' | 'y' | 'z';

const AXES: Axis[] = ['x', 'y', 'z'];
const AXIS_LABEL: Record<Axis, string> = { x: 'X', y: 'Y', z: 'Z' };

// Una direzione conta come un asse se ci sta praticamente sopra: i numeri arrivano da una catena
// di matrici e tornano con l'ultima cifra sporca.
const ALIGNED = 0.999;

type Row = [number, number, number];

// Babylon tiene la matrice per righe, con la traslazione in fondo: le prime tre righe sono
// l'immagine degli assi locali, che è esattamente quello che serve qui.
const linearRows = (matrix: any): [Row, Row, Row] | null => {
    const m = matrix?.m;
    if (!m || m.length < 11) return null;

    return [
        [m[0], m[1], m[2]],
        [m[4], m[5], m[6]],
        [m[8], m[9], m[10]]
    ];
};

// Il determinante negativo È lo specchio: nessuna rotazione, per quanto composta, può ribaltarlo.
const determinant = ([a, b, c]: [Row, Row, Row]) =>
    a[0] * (b[1] * c[2] - b[2] * c[1]) - a[1] * (b[0] * c[2] - b[2] * c[0]) + a[2] * (b[0] * c[1] - b[1] * c[0]);

// QUALE CAMPO RISPONDE AL CONTRARIO, chiesto alla matrice invece che indovinato. L'asse locale i si
// vede lungo la riga i, e in uno specchio i due gesti si rovesciano all'opposto l'uno dell'altro:
// se la riga punta nel verso NEGATIVO dell'asse, è lo spostamento a partire dalla parte sbagliata;
// se punta nel verso positivo, i due rovesciamenti si annullano sullo spostamento e a girare al
// contrario è la rotazione.
//
// SI NOMINANO GLI ASSI SOLO SE OGNUNO È RIMASTO SÉ STESSO. Basta che un genitore ruotato porti la X
// di questo oggetto lungo la Y del mondo e «su X» non vuol più dire niente per chi guarda: lì il
// testo resta sul generico, che è vero sempre.
const reversedSenses = (rows: [Row, Row, Row]): { movement: string[]; rotation: string[] } | null => {
    const movement: string[] = [];
    const rotation: string[] = [];

    for (let index = 0; index < rows.length; index++) {
        const row = rows[index];
        const length = Math.hypot(row[0], row[1], row[2]);
        if (!length) return null;

        if (Math.abs(row[index]) / length < ALIGNED) return null;

        if (row[index] > 0) rotation.push(AXIS_LABEL[AXES[index]]);
        else movement.push(AXIS_LABEL[AXES[index]]);
    }

    return { movement, rotation };
};

// «Y e Z», non «Y, Z»: sono due assi, non un elenco, e il testo lo legge una persona.
const listOf = (labels: string[]) => (labels.length > 1 ? `${labels.slice(0, -1).join(', ')} ${T._('and', 'Admin')} ${labels[labels.length - 1]}` : labels[0]);

// QUELLO CHE UNA PERSONA DEVE SAPERE, in quest'ordine: che cosa le sta succedendo sotto le dita,
// da dove viene, e se tocca a lei rimediare. Il nome del nodo non c'è — nelle scene vere è un
// codice — e non ci sono i gradi della compensazione: sapere che sono 180 su Z non aiuta a
// decidere niente, e leggendoli sembrava che a essere girato di 180° fosse questo oggetto.
const senseDetail = (senses: { movement: string[]; rotation: string[] }): string => {
    const { movement, rotation } = senses;

    if (movement.length && rotation.length)
        return stringFormat(
            T._('Here, moving along {0} and turning around {1} go the opposite way to what you see on screen.', 'Admin'),
            listOf(movement),
            listOf(rotation)
        );

    if (rotation.length) return stringFormat(T._('Here, turning around {0} goes the opposite way to what you see on screen.', 'Admin'), listOf(rotation));

    return stringFormat(T._('Here, moving along {0} goes the opposite way to what you see on screen.', 'Admin'), listOf(movement));
};

/**
 * La spiegazione da mostrare accanto alle trasformate di questo oggetto — un capoverso per riga —
 * o `null` se non c'è niente da spiegare, che è il caso di tutto ciò che non sta in un ramo
 * specchiato.
 */
export const describeMirroredSpace = (mesh: any): string[] | null => {
    const rows = linearRows(mesh?.parent?.getWorldMatrix?.());
    if (!rows || determinant(rows) >= 0) return null;

    const senses = reversedSenses(rows);

    const detail = senses
        ? senseDetail(senses)
        : T._('Here, a value you type can move or turn the object the opposite way to what you see on screen.', 'Admin');

    const why = T._(
        'This model was built in a coordinate system different from the one of the scene, so when it was imported it was placed under a node that mirrors it. Everything below that node, including this object, is mirrored with it.',
        'Admin'
    );

    const advice = T._(
        'There is nothing to fix here: the values are stored correctly and that node has to stay as it is, because the configurator, AR and Virtual Try-On read the model through it.',
        'Admin'
    );

    return [detail, why, advice];
};
